class BundleSliderCart {
  constructor(element) {
    this.element = element;
    this.products = [];
    this.originalColumns = null;
    this.collection = null;
    
    this.init();
  }

  init() {
    this.collection = this.element.closest('collection-component');
    if (!this.collection || this.collection.getAttribute('data-enable-bundle-builder') !== 'true') {
      return;
    }

    // Store original columns value for pick-and-mix template
    if (this.collection.getAttribute('data-pick-and-mix') === 'true') {
      this.storeOriginalColumns();
    }

    document.addEventListener('theme:cart:add', this.handleAddToSliderCart.bind(this), true);
    
    const btn = this.element.querySelector('[data-bundle-slider-cart-button]');
    if (btn) btn.addEventListener('click', this.handleAddAllToCart.bind(this));
    
    this.setupMobileToggle();
    
    // Handle window resize to adjust columns when switching between mobile/desktop
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.adjustGridColumns();
      }, 100);
    });
    
    this.updateVisibility();
  }

  storeOriginalColumns() {
    if (!this.collection) return;
    
    const computedStyle = window.getComputedStyle(this.collection);
    const columns = computedStyle.getPropertyValue('--COLUMNS').trim();
    
    if (columns && !this.originalColumns) {
      this.originalColumns = parseFloat(columns);
    }
  }

  adjustGridColumns() {
    // Only adjust on desktop (min-width: 750px) and for pick-and-mix template
    if (window.innerWidth < 750) return;
    if (!this.collection || this.collection.getAttribute('data-pick-and-mix') !== 'true') return;
    if (this.originalColumns === null) {
      this.storeOriginalColumns();
    }
    if (this.originalColumns === null) return;

    const hasProducts = this.products.length > 0;
    const newColumns = hasProducts ? Math.max(1, this.originalColumns - 1) : this.originalColumns;
    
    this.collection.style.setProperty('--COLUMNS', newColumns.toString());
  }

  setupMobileToggle() {
    const header = this.element.querySelector('.bundle-slider-cart__header');
    const closeBtn = this.element.querySelector('[data-bundle-slider-cart-close]');
    
    if (header) {
      header.addEventListener('click', (e) => {
        if (window.innerWidth > 749) {
          return;
        }
        
        if (e.target.closest('[data-bundle-slider-cart-close]')) {
          return;
        }
        
        const isExpanded = this.element.getAttribute('data-expanded') === 'true';
        this.element.setAttribute('data-expanded', isExpanded ? 'false' : 'true');
      });
    }
    
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.element.setAttribute('data-expanded', 'false');
      });
    }
  }

  handleAddToSliderCart(event) {
    const collection = this.element.closest('collection-component');
    if (!collection || collection.getAttribute('data-enable-bundle-builder') !== 'true') {
      return;
    }

    event.preventDefault(); 
    event.stopPropagation();

    const button = event.detail.button;
    if (!button || button.disabled) return;

    const form = button.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;

    const product = this.getProduct(form, variantId, formData);
    if (!product) return;

    // Check if this variant already exists in the bundle cart
    const existingProductIndex = this.products.findIndex(p => p.variantId === variantId);
    
    if (existingProductIndex !== -1) {
      // Increment quantity if variant already exists
      this.products[existingProductIndex].quantity += 1;
    } else {
      // Add new product with quantity 1
      product.quantity = 1;
      this.products.push(product);
    }
    
    // Trigger shake animation on bag icon
    this.triggerBagIconShake();
    
    this.updateData();
    
    const quickAddModal = form.closest('[data-quick-add-modal]');
    if (quickAddModal) {
      quickAddModal.dispatchEvent(new CustomEvent('theme:modal:close', { bubbles: false }));
      
      if (typeof quickAddModal.close === 'function') {
        quickAddModal.close();
      } else {
        quickAddModal.removeAttribute('open');
      }
      
      quickAddModal.removeAttribute('closing');
      quickAddModal.setAttribute('inert', '');
      
      setTimeout(() => {
        const openModals = document.querySelectorAll('dialog[open][data-scroll-lock-required]');
        const activeModals = Array.from(openModals).filter(modal => !modal.hasAttribute('inert'));
        
        if (activeModals.length === 0) {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        }
      }, 200);
    }
    
    if (button) {
      button.classList.add('is-added');
      setTimeout(() => {
        button.classList.remove('is-added');
      }, 1000);
    }
  }

  getProduct(form, variantId, formData) {
    let productItem = form.closest('.product-item');
    
    if (!productItem) {
      const quickAddModal = form.closest('[data-quick-add-modal]');
      if (quickAddModal) {
        const productId = quickAddModal.getAttribute('data-product-id');
        if (productId) {
          const quickAddHolder = document.querySelector(`[data-quick-add-holder="${productId}"]`);
          productItem = quickAddHolder?.closest('.product-item');
        }
      }
    }
    
    if (!productItem) return null;

    const variantData = productItem.querySelector(`[data-variant-id="${variantId}"]`);
    const variantPicker = productItem.querySelector('variant-picker');
    const variantDisplay = variantPicker?.querySelector('[data-variant-display]');

    return {
      id: variantId,
      variantId,
      title: productItem.querySelector('.product-item__title')?.textContent?.trim() || 'Product',
      vendor: productItem.querySelector('.product-item__vendor')?.textContent?.trim() || '',
      variantTitle: variantData?.getAttribute('data-variant-title') || variantDisplay?.textContent?.trim() || '',
      price: parseInt(variantData?.getAttribute('data-variant-price') || '0'),
      image: productItem.querySelector('img')?.src || '',
      formData: formData
    };
  }

  
  updateData() {
    this.element.setAttribute('data-products', JSON.stringify(this.products));
    this.renderProducts();
    this.updateCartCount();
    this.updateAddButton();
    this.updateTotal();
    this.updateVisibility();
    
    this.element.dispatchEvent(new CustomEvent('bundle:products:updated', {
      detail: { products: this.products },
      bubbles: true
    }));
  }

  updateVisibility() {
    if (this.products.length > 0) {
      this.element.setAttribute('data-has-products', 'true');
    } else {
      this.element.removeAttribute('data-has-products');
    }
    
    // Adjust grid columns when cart visibility changes (for pick-and-mix template)
    this.adjustGridColumns();
  }

  triggerBagIconShake() {
    const iconContainer = this.element.querySelector('.bundle-slider-cart__header__icon');
    if (iconContainer) {
      iconContainer.classList.add('shake');
      // Remove the class after animation completes to allow it to be triggered again
      setTimeout(() => {
        iconContainer.classList.remove('shake');
      }, 800);
    }
  }

  updateAddButton() {
    const button = this.element.querySelector('[data-bundle-slider-cart-button]');
    if (button) {
      button.disabled = this.products.length === 0;
    }
  }

  updateTotal() {
    const totalElement = this.element.querySelector('[data-cart-total]');
    if (totalElement) {
      const total = this.products.reduce((sum, product) => {
        const quantity = product.quantity || 1;
        return sum + ((product.price || 0) * quantity);
      }, 0);
      const formattedPrice = window.theme && window.theme.formatMoney 
        ? window.theme.formatMoney(total, window.theme.moneyFormat || '${{amount}}')
        : '$' + (total / 100).toFixed(2);
      totalElement.textContent = formattedPrice;
    }
  }

  renderProducts() {
    const productContainer = this.element.querySelector('[data-bundle-products]');
    const template = document.getElementById('bundle-product-template');
    if (!productContainer || !template) return;

    // Get all existing product elements
    const existingProducts = Array.from(productContainer.querySelectorAll('bundle-product'));
    const existingVariantIds = new Set(existingProducts.map(el => el.getAttribute('data-variant-id')));
    const currentVariantIds = new Set(this.products.map(p => p.variantId));
    
    // Remove products that are no longer in the array
    existingProducts.forEach(existingProduct => {
      const variantId = existingProduct.getAttribute('data-variant-id');
      if (!currentVariantIds.has(variantId)) {
        existingProduct.remove();
      }
    });
    
    // Update or create products
    this.products.forEach((product, index) => {
      const existingProduct = productContainer.querySelector(`bundle-product[data-variant-id="${product.variantId}"]`);
      
      if (existingProduct) {
        // Update existing product in place
        this.updateProductElement(existingProduct, product);
      } else {
        // Create new product element
        const clone = template.content.cloneNode(true);
        const bundleProduct = clone.querySelector('bundle-product');
        
        if (bundleProduct) {
          bundleProduct.setAttribute('data-variant-id', product.variantId);
          this.populateProductElement(bundleProduct, product);
          productContainer.appendChild(clone);
        }
      }
    });
  }

  updateProductElement(element, product) {
    const vendor = element.querySelector('.bundle-product__vendor');
    const title = element.querySelector('.bundle-product__title');
    const variantTitle = element.querySelector('.bundle-product__variant');
    const price = element.querySelector('.bundle-product__price');
    const image = element.querySelector('.bundle-product__image');
    const quantityPicker = element.querySelector('bundle-quantity-picker');
    
    if (vendor) vendor.textContent = product.vendor;
    if (title) title.textContent = product.title;
    if (variantTitle && product.variantTitle) variantTitle.textContent = `(${product.variantTitle})`;
    
    // Update quantity in picker
    if (quantityPicker) {
      const quantity = product.quantity || 1;
      quantityPicker.setAttribute('data-variant-id', product.variantId);
      quantityPicker.setAttribute('data-quantity', quantity.toString());
      
      // Update display value
      const quantityDisplay = quantityPicker.querySelector('[data-quantity-display]');
      if (quantityDisplay) {
        quantityDisplay.textContent = quantity.toString();
      }
      
      // Update disabled state for decrease button
      const decreaseBtn = quantityPicker.querySelector('[data-quantity-nav="prev"]');
      if (decreaseBtn) {
        const isDisabled = quantity <= 1;
        decreaseBtn.disabled = isDisabled;
        decreaseBtn.classList.toggle('picker__btn--disabled', isDisabled);
      }
    }
    
    // Calculate total price for this product (price * quantity)
    if (price && product.price) {
      const quantity = product.quantity || 1;
      const totalPrice = product.price * quantity;
      const formattedPrice = window.theme && window.theme.formatMoney 
        ? window.theme.formatMoney(totalPrice, window.theme.moneyFormat || '${{amount}}')
        : '$' + (totalPrice / 100).toFixed(2);
      price.textContent = formattedPrice;
    }
    
    if (image) {
      image.src = product.image;
      image.alt = product.title;
    }
  }

  populateProductElement(element, product) {
    const vendor = element.querySelector('.bundle-product__vendor');
    const title = element.querySelector('.bundle-product__title');
    const variantTitle = element.querySelector('.bundle-product__variant');
    const price = element.querySelector('.bundle-product__price');
    const removeBtn = element.querySelector('[data-bundle-remove]');
    const image = element.querySelector('.bundle-product__image');
    const quantityPicker = element.querySelector('bundle-quantity-picker');
    const picker = element.querySelector('.picker');
    
    if (vendor) vendor.textContent = product.vendor;
    if (title) title.textContent = product.title;
    if (variantTitle && product.variantTitle) variantTitle.textContent = `(${product.variantTitle})`;
    
    // Set quantity in picker
    if (picker) picker.setAttribute('data-variant-id', product.variantId);
    if (quantityPicker) {
      const quantity = product.quantity || 1;
      quantityPicker.setAttribute('data-variant-id', product.variantId);
      quantityPicker.setAttribute('data-quantity', quantity.toString());
      
      // Set initial display value
      const quantityDisplay = quantityPicker.querySelector('[data-quantity-display]');
      if (quantityDisplay) {
        quantityDisplay.textContent = quantity.toString();
      }
      
      // Set initial disabled state for decrease button (disabled when quantity is 1)
      const decreaseBtn = quantityPicker.querySelector('[data-quantity-nav="prev"]');
      if (decreaseBtn) {
        const isDisabled = quantity <= 1;
        decreaseBtn.disabled = isDisabled;
        decreaseBtn.classList.toggle('picker__btn--disabled', isDisabled);
      }
    }
    
    // Calculate total price for this product (price * quantity)
    if (price && product.price) {
      const quantity = product.quantity || 1;
      const totalPrice = product.price * quantity;
      const formattedPrice = window.theme && window.theme.formatMoney 
        ? window.theme.formatMoney(totalPrice, window.theme.moneyFormat || '${{amount}}')
        : '$' + (totalPrice / 100).toFixed(2);
      price.textContent = formattedPrice;
    }
    
    if (image) {
      image.src = product.image;
      image.alt = product.title;
    }
    
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeProduct(product.variantId);
      });
    }
  }

  removeProduct(variantId) {
    const index = this.products.findIndex(p => p.variantId === variantId);
    if (index !== -1) {
      this.products.splice(index, 1);
      this.updateData();
      // Grid columns will be adjusted in updateVisibility()
    }
  }

  updateProductQuantity(variantId, newQuantity) {
    const product = this.products.find(p => p.variantId === variantId);
    if (product) {
      if (newQuantity <= 0) {
        this.removeProduct(variantId);
      } else {
        product.quantity = newQuantity;
        this.updateData();
      }
    }
  }

  updateCartCount() {
    const countElement = this.element.querySelector('[data-cart-count]');
    if (countElement) {
      const totalItems = this.products.reduce((sum, product) => sum + (product.quantity || 1), 0);
      countElement.textContent = totalItems;
    }
  }

  async handleAddAllToCart(event) {
    event.preventDefault();
    
    if (this.products.length === 0) return;

    const button = event.target.closest('[data-bundle-slider-cart-button]');
    if (!button) return;

    button.disabled = true;
    button.classList.add('loading');
    
    const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
    const cartChangeUrl = window.theme?.routes?.cart_change_url || '/cart/change.js';
    const cartUrl = window.theme?.routes?.cart_url || '/cart';
    
    try {
      for (const product of this.products) {
        const variantId = product.variantId;
        const formData = product.formData;
        const quantity = product.quantity || 1;
        
        const cartResponse = await fetch(cartUrl + '.js', {
          headers: {
            'Accept': 'application/json',
          }
        });
        
        const cart = await cartResponse.json();
        const existingItems = cart.items || [];
        
        const existingItem = existingItems.find(item => item.variant_id === parseInt(variantId));
        
        if (existingItem) {
          const newQuantity = existingItem.quantity + quantity;
          
          const response = await fetch(cartChangeUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              id: existingItem.key,
              quantity: newQuantity
            })
          });

          const result = await response.json();
          
          if (result.errors) {
            throw new Error(Array.isArray(result.errors) ? result.errors.join(', ') : result.errors || 'Failed to update product quantity');
          }
        } else {
          // Clone FormData and update quantity
          const clonedFormData = new FormData();
          for (const [key, value] of formData.entries()) {
            clonedFormData.append(key, value);
          }
          clonedFormData.set('quantity', quantity.toString());
          
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json',
            },
            body: clonedFormData
          });

          const result = await response.json();
          
          if (!response.ok || result.status) {
            throw new Error(result.description || 'Failed to add product to cart');
          }
        }
      }

      this.products = [];
      this.updateData();
      
      document.dispatchEvent(new CustomEvent('theme:cart:update'));
      
      const cartComponent = document.querySelector('cart-items');
      if (cartComponent && cartComponent.getCart) {
        cartComponent.getCart();
      }
      
      button.classList.remove('loading');
      button.classList.add('added');
      
      setTimeout(() => {
        button.classList.remove('added');
      }, 2000);
      
      if (window.theme?.settings?.cartType === 'page') {
        window.location = cartUrl;
      }
      
    } catch (error) {
      console.error('Error adding products to cart:', error);
      button.disabled = false;
      button.classList.remove('loading');
      alert(error.message || 'Failed to add products to cart. Please try again.');
    }
  }
}

// BundleQuantityPicker custom element for managing quantity in bundle cart
class BundleQuantityPicker extends HTMLElement {
  constructor() {
    super();
    this.quantity = 1;
    this.variantId = null;
  }

  connectedCallback() {
    this.variantId = this.getAttribute('data-variant-id');
    this.quantity = parseInt(this.getAttribute('data-quantity') || '1');
    
    const decreaseBtn = this.querySelector('[data-quantity-nav="prev"]');
    const increaseBtn = this.querySelector('[data-quantity-nav="next"]');
    
    if (decreaseBtn) decreaseBtn.addEventListener('click', () => this.changeQuantity(-1));
    if (increaseBtn) increaseBtn.addEventListener('click', () => this.changeQuantity(1));
    
    this.updateDisplay();
  }

  getBundleCartInstance() {
    const cartElement = this.closest('bundle-slider-cart') || document.querySelector('bundle-slider-cart');
    return cartElement?.bundleSliderCartInstance || null;
  }

  changeQuantity(delta) {
    const newQuantity = this.quantity + delta;
    if (newQuantity < 1) return;
    
    this.quantity = newQuantity;
    this.setAttribute('data-quantity', this.quantity.toString());
    this.updateDisplay();
    
    const bundleCartInstance = this.getBundleCartInstance();
    if (bundleCartInstance && this.variantId) {
      bundleCartInstance.updateProductQuantity(this.variantId, this.quantity);
      // Trigger shake animation when increasing quantity (adding more items)
      if (delta > 0) {
        bundleCartInstance.triggerBagIconShake();
      }
    }
  }

  updateDisplay() {
    const quantityDisplay = this.querySelector('[data-quantity-display]');
    const decreaseBtn = this.querySelector('[data-quantity-nav="prev"]');
    
    if (quantityDisplay) quantityDisplay.textContent = this.quantity.toString();
    
    if (decreaseBtn) {
      const isDisabled = this.quantity <= 1;
      decreaseBtn.disabled = isDisabled;
      decreaseBtn.classList.toggle('picker__btn--disabled', isDisabled);
    }
  }

  static get observedAttributes() {
    return ['data-quantity'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-quantity' && oldValue !== newValue) {
      this.quantity = parseInt(newValue || '1');
      this.updateDisplay();
    }
  }
}

// Register the custom element
customElements.define('bundle-quantity-picker', BundleQuantityPicker);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const element = document.querySelector('bundle-slider-cart');
    if (element) {
      const instance = new BundleSliderCart(element);
      element.bundleSliderCartInstance = instance;
    }
  });
} else {
  const element = document.querySelector('bundle-slider-cart');
  if (element) {
    const instance = new BundleSliderCart(element);
    element.bundleSliderCartInstance = instance;
  }
}
